/**
 * Login page component.
 * 
 * Features:
 * - Google SSO login button
 * - Branded login experience
 * - Redirects to home if already logged in
 * 
 * Called by:
 * - App.tsx as route for /login
 */
import { Navigate, useLocation } from 'react-router-dom';
import { Card, Button, Typography, Space } from 'antd';
import { GoogleOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { getLoginUrl } from '../services/api';

const { Title, Paragraph, Text } = Typography;

/**
 * Login page component.
 * Displays login form with Google SSO button.
 */
function LoginPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // If already logged in, redirect to home or original destination
  if (!isLoading && isAuthenticated) {
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }

  /**
   * Handle login button click.
   * Redirects to Google OAuth flow.
   */
  function handleLogin() {
    window.location.href = getLoginUrl();
  }

  // Check for error in URL params
  const searchParams = new URLSearchParams(location.search);
  const authError = searchParams.get('error');

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#121212',
        padding: '24px',
      }}
    >
      <Card
        style={{
          maxWidth: '420px',
          width: '100%',
          backgroundColor: '#1a1a1a',
          borderColor: '#333',
          textAlign: 'center',
        }}
        styles={{
          body: { padding: '48px 32px' },
        }}
      >
        {/* Logo and title */}
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title
              level={1}
              style={{
                fontFamily: 'var(--font-serif)',
                fontWeight: 400,
                marginBottom: '8px',
                fontSize: '2.5rem',
              }}
            >
              Compendium
            </Title>
            <Paragraph style={{ color: '#a0a0a0', marginBottom: 0 }}>
              Essay Writing & Collaborative Review
            </Paragraph>
          </div>

          {/* Error message if auth failed */}
          {authError && (
            <div
              style={{
                padding: '12px',
                backgroundColor: 'rgba(255, 77, 79, 0.1)',
                borderRadius: '6px',
                border: '1px solid rgba(255, 77, 79, 0.3)',
              }}
            >
              <Text type="danger">
                {authError === 'auth_failed'
                  ? 'Authentication failed. Please try again.'
                  : 'An error occurred. Please try again.'}
              </Text>
            </div>
          )}

          {/* Login button */}
          <Button
            type="primary"
            size="large"
            icon={<GoogleOutlined />}
            onClick={handleLogin}
            style={{
              width: '100%',
              height: '48px',
              fontSize: '1rem',
            }}
          >
            Sign in with Google
          </Button>

          {/* Info text */}
          <div style={{ marginTop: '16px' }}>
            <Text type="secondary" style={{ fontSize: '0.85rem' }}>
              New users will need approval from the administrator before accessing the platform.
            </Text>
          </div>
        </Space>
      </Card>
    </div>
  );
}

export default LoginPage;
